import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountCardListStore, $transferReasonsStore } from '@lib/store';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type AccountResponse, AccountType, TransferRequestTypeEnum } from '@shared/api';
import { OtherAccountTab, OwnAccountTab } from './widgets';
import { PageContainer, SelectAccount } from '@widgets';
import { Icon, Tabs, Text } from '@shared/widgets';
import ArrowIcon from '@assets/icons/arrow.svg';
import ArrowVCFCIcon from '@assets/icons/arrow-vcfc.svg';

export const PaymentBetweenAccountsPage = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  const accountStore = useUnit($accountCardListStore.store);

  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [tab, setTab] = useState<TransferRequestTypeEnum>(
    type ? TransferRequestTypeEnum.OtherInsigniaAccount : TransferRequestTypeEnum.BetweenInsigniaAccount,
  );

  const { t } = useTranslation('payments');

  useEffect(() => {
    Promise.allSettled([
      $accountCardListStore.events.getAccounts({ AccountType: AccountType.Payment }),
      $transferReasonsStore.events.getTransferReasons(),
    ]);
    return () => $accountCardListStore.events.clear();
  }, []);

  useEffect(() => {
    if (accountStore.data.length > 0) setAccount(accountStore.data[0]);
  }, [accountStore.data]);

  const TABS = useMemo(
    () => [
      { label: t('betweenAccounts.tabs.betweenAccounts'), value: TransferRequestTypeEnum.BetweenInsigniaAccount },
      {
        label: t(`betweenAccounts.tabs.${PaletteUtils.isVcfcPalette ? 'otherVcfcAccount' : 'otherInsigniaAccount'}`),
        value: TransferRequestTypeEnum.OtherInsigniaAccount,
      },
    ],
    [t, PaletteUtils.isVcfcPalette],
  );
  return (
    <PageContainer
      title={t('title')}
      header={
        <Link className='group flex items-center gap-2' to={RoutePaths.payments.onNavigate()}>
          <Icon
            className={cn(PaletteUtils.isInsigniaPalette && 'rotate-180')}
            icon={PaletteUtils.isInsigniaPalette ? <ArrowIcon /> : <ArrowVCFCIcon className='text-black dark:text-white' />}
          />
          {PaletteUtils.isInsigniaPalette ? (
            <Text weight='semibold' size='2xl'>
              {t('header')}
            </Text>
          ) : (
            <Text className='group-hover:underline' size='sm'>
              {t('return')}
            </Text>
          )}
        </Link>
      }
      return
    >
      <main
        className={cn(
          'flex flex-col gap-6',
          PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        )}
      >
        <Text weight={PaletteUtils.getPaletteParam('semibold')} size='2xl'>
          {t('betweenAccounts.title')}
        </Text>
        <SelectAccount
          label={t('betweenAccounts.placeholders.transferFrom')}
          loading={accountStore.loading}
          type={AccountType.Payment}
          accounts={accountStore.data}
          value={account}
          onChange={setAccount}
        />
        <Tabs
          className={cn('bg-white dark:bg-soft-black', PaletteUtils.isInsigniaPalette && '-mb-6 p-6 pb-0')}
          options={TABS}
          value={tab}
          onChange={setTab}
        />
        {tab == TransferRequestTypeEnum.BetweenInsigniaAccount && <OwnAccountTab account={account} />}
        {tab == TransferRequestTypeEnum.OtherInsigniaAccount && <OtherAccountTab account={account} />}
      </main>
    </PageContainer>
  );
};
